import { useEffect, useRef, useState } from 'react'
import { ArrowUp, ArrowDown, GripVertical, X, Loader2 } from 'lucide-react'
import { driverApi } from '@/api/drivers'
import type { Driver } from '@/types'
import { Button } from '@/components/ui/button'

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
}

/** 拖移（或按上下箭頭）調整司機順序，存檔後新增/編輯派車單的「指派司機」選單就照這個順序 */
export default function DriverOrderModal({ open, onClose, onSaved }: Props) {
  const [items, setItems] = useState<Driver[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const dragIndex = useRef<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setError('')
    driverApi.getDrivers(1, 100)
      .then(res => setItems(res.items || []))
      .catch(() => setError('載入司機失敗，請稍後再試'))
      .finally(() => setLoading(false))
  }, [open])

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return
    setItems(prev => {
      const next = [...prev]
      const [it] = next.splice(from, 1)
      next.splice(to, 0, it)
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await driverApi.reorderDrivers(items.map(d => d.id))
      onSaved()
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.detail || '儲存順序失敗，請稍後再試')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md max-h-[85vh] flex flex-col rounded-xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <div className="font-bold text-gray-900 dark:text-white">調整司機順序</div>
            <div className="text-xs text-gray-500">拖移或按箭頭，指派司機的選單會照這個順序</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : items.map((d, i) => (
            <div
              key={d.id}
              draggable
              onDragStart={() => { dragIndex.current = i }}
              onDragOver={e => { e.preventDefault(); setOverIndex(i) }}
              onDragEnd={() => { dragIndex.current = null; setOverIndex(null) }}
              onDrop={() => {
                if (dragIndex.current !== null) move(dragIndex.current, i)
                dragIndex.current = null
                setOverIndex(null)
              }}
              className={`flex items-center gap-2 rounded-lg border px-2 py-2 bg-white dark:bg-gray-900 ${
                overIndex === i ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <GripVertical className="w-4 h-4 text-gray-400 cursor-grab shrink-0" />
              <span className="w-6 text-xs text-gray-400 text-right shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {d.driver_category === 'external' ? '[外] ' : ''}{d.name}
                </div>
                <div className="text-xs text-gray-500 truncate">{d.vehicle_plate}</div>
              </div>
              <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 cursor-pointer">
                <ArrowUp className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => move(i, i + 1)} disabled={i === items.length - 1}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 cursor-pointer">
                <ArrowDown className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {error && <p className="px-4 pb-2 text-sm text-red-500">{error}</p>}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleSave} disabled={saving || loading || items.length === 0}>
            {saving ? '儲存中...' : '儲存順序'}
          </Button>
        </div>
      </div>
    </div>
  )
}
